import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comicCategoryNames = comic.categories
    .map((slug) => categories.find((c) => c.slug === slug))
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 57漫画网在线免费阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '57漫画网',
      url: 'https://57manhua.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://57manhua.com/comic/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '漫画分类', to: '/category/shaonian' },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 w-full md:w-[280px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} - 57漫画网在线免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </div>
                  {comic.rating} 分
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comicCategoryNames.map((cat) =>
                  cat ? (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ) : null
                )}
              </div>

              <div className="flex items-center gap-6 mt-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {(comic.views / 10000).toFixed(0)} 万阅读
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line"></i>
                  </div>
                  {(comic.favorites / 10000).toFixed(0)} 万收藏
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <Link
                  to={`/comic/${comic.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const firstChapter = comic.chapters[0];
                    if (firstChapter) setSelectedChapter(firstChapter.id);
                  }}
                  className="px-8 py-2.5 rounded-full bg-primary-500 text-background-50 font-bold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors duration-200">
                  <i className="ri-heart-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-600 transition-colors duration-200">
                  <i className="ri-share-forward-line text-lg"></i>
                </button>
              </div>

              <div className="mt-6 p-4 md:p-5 bg-background-100 rounded-xl">
                <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full w-fit mb-6">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                章节列表
              </button>
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                作品信息
              </button>
            </div>

            {activeTab === 'chapters' && (
              <div>
                {selectedChapter ? (
                  <div>
                    <button
                      onClick={() => setSelectedChapter(null)}
                      className="mb-4 flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line"></i>
                      </div>
                      返回章节列表
                    </button>
                    <div className="bg-background-100 rounded-xl p-6 md:p-10 text-center">
                      <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                        {comic.chapters.find((c) => c.id === selectedChapter)?.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mb-6">
                        {comic.title} · {comic.author}
                      </p>
                      <div className="max-w-2xl mx-auto">
                        <img
                          src={comic.coverImage}
                          alt={`${comic.title} - 漫画内容预览`}
                          className="w-full rounded-lg"
                        />
                        <p className="mt-6 text-sm text-foreground-500 leading-relaxed">
                          此为漫画阅读区域预览。在57漫画网完整版中，您将看到完整的漫画内容，支持滚动阅读和翻页模式。
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-6">
                        <button className="px-5 py-2 rounded-full bg-background-200 text-foreground-600 text-sm font-medium hover:bg-background-300 transition-colors duration-200 whitespace-nowrap">
                          上一话
                        </button>
                        <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
                          下一话
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                    {[...comic.chapters].reverse().map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => setSelectedChapter(chapter.id)}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-background-100 transition-all duration-200 text-left group"
                      >
                        <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 font-medium">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400 ml-2 whitespace-nowrap">{chapter.updateDate}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'info' && (
              <div className="bg-background-100 rounded-xl p-5 md:p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-bold text-foreground-950 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-foreground-400">作者</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{comic.author}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">状态</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{comic.status}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">评分</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{comic.rating} 分</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">总阅读量</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{(comic.views / 10000).toFixed(0)} 万</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">总收藏数</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{(comic.favorites / 10000).toFixed(0)} 万</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">最近更新</span>
                    <p className="text-sm font-medium text-foreground-950 mt-1">{comic.updateDate}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}